import { diseaseAPI } from "../../apis";
import { healthScoreGroups } from "../../utils/constants";
import {
  epochToIST,
  epochToISTDate,
  getColorByScoreLabel,
  getScoreColor,
  processHealthScore,
} from "../../utils/eira/helper";
import { getHealthHistory } from "../../utils/eira/scorehelper";
import {
  ApiResponse,
  ApiResponseData,
  AppointmentsResponse,
  AyurvedicCornerConfig,
  AyurvedicCornerResult,
  CommonAPIResponse,
  DashboardData,
  DoctorsResponse,
  HealthHistoryData,
  HealthScoreGroupItem,
  HealthScoreResult,
  HealthScoresApiResponse,
  PrakritiApiResponse,
  PrakritiResult,
  PrescriptionEntry,
  ProcessedAppointment,
  ProcessedDoctor,
  ProcessedPrescription,
  ProcessedReport,
  ProcessedSymptomEntry,
  ProcessedUserDetails,
  ReportEntry,
  ResponseMapping,
  ScoreNameWithPrefix,
  UserDetailsAPIResponse,
  UserDetailsEntry,
  UserSymptomEntry,
} from "./interface";

// Common interfaces

type SymptomsResponse = CommonAPIResponse<UserSymptomEntry>;
type ReportsResponse = CommonAPIResponse<ReportEntry>;
type UserDetailsResponse = CommonAPIResponse<UserDetailsEntry>;

class UserDashboardData {
  private userId: string;
  private client: string;

  constructor(userId: string, client: string) {
    this.userId = userId;
    this.client = client;
  }

  // Utility method to fetch all paginated data
  private async fetchAllPaginatedData<T>(
    fetchFunction: (
      page: number,
      pageSize: number
    ) => Promise<CommonAPIResponse<T>>,
    isfetchLatest: boolean
  ): Promise<T[]> {
    if (isfetchLatest) {
      const response = await fetchFunction(1, 1);
      return response.data;
    }

    let allData: T[] = [];
    let currentPage = 1;
    const pageSize = 30;
    let totalPages = 1;

    do {
      const response = await fetchFunction(currentPage, pageSize);
      allData = allData.concat(response.data);
      totalPages = response.meta.pagination.pageCount;
      currentPage++;
    } while (currentPage <= totalPages);

    return allData;
  }

  // Private methods
  public getUserDetails = async (): Promise<ProcessedUserDetails | null> => {
    try {
      const response = await diseaseAPI.getUserDetailsforDashboard(
        this.userId,
        this.client
      );

      let userDetails: UserDetailsAPIResponse;

      // Handle different response formats
      if (response && "data" in response && Array.isArray(response.data)) {
        // Format 1: CommonAPIResponse format with data array
        if (response.data.length === 0) {
          return null;
        }
        userDetails = response.data[0];
      } else if (response && "attributes" in response && "id" in response) {
        // Format 2: Direct object format (your current response)
        userDetails = response as UserDetailsAPIResponse;
      } else if (
        response &&
        "data" in response &&
        !Array.isArray(response.data) &&
        response.data &&
        "attributes" in response.data
      ) {
        // Format 3: Single object wrapped in data property

        userDetails = response.data as UserDetailsAPIResponse;
      } else {
        return null;
      }

      // Validate required fields
      if (!userDetails.id || !userDetails.attributes) {
        return null;
      }

      const processedUserDetails: ProcessedUserDetails = {
        id: userDetails.id,
        userId: userDetails.attributes.user_id,
        name: userDetails.attributes.name || "",
        age: userDetails.attributes.age || 0,
        gender: userDetails.attributes.gender || "",
        phoneNumber: userDetails.attributes.phone_number || "",
        email: userDetails.attributes.email || "",
        weight: userDetails.attributes.weight_in_kg || 0,
        height: userDetails.attributes.height_in_cms || 0,
        waistCircumference:
          userDetails.attributes.waist_circumference_in_cms || 0,
        placeOfBirth: userDetails.attributes.place_of_birth,
        timeOfBirth: userDetails.attributes.time_of_birth || "",
        currentLocation: userDetails.attributes.current_location,
        bloodGroup: userDetails.attributes.blood_group || "",
        maritalStatus: userDetails.attributes.martial_status || "",
        emergencyContactNumber:
          userDetails.attributes.emergency_contact_number || "",
        emergencyContactName:
          userDetails.attributes.emergency_contact_name || "",
        client: userDetails.attributes.client || "",
        diseases: userDetails.attributes.diseases,
        createdAt: userDetails.attributes.createdAt || "",
        updatedAt: userDetails.attributes.updatedAt || "",
        publishedAt: userDetails.attributes.publishedAt || "",
        employmentStatus: userDetails.attributes.employment_status || "",
        employmentType: userDetails.attributes.employment_type || "",
      };

      return processedUserDetails;
    } catch (error) {
      console.error("❌ Error fetching user details:", error);

      // Log more details about the error
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }

      return null;
    }
  };

  private getUserDashboardSymptoms = async (
    isfetchLatest: boolean = false,
    isfetchAll: boolean = false
  ): Promise<ProcessedSymptomEntry[]> => {
    try {
      const fetchFunction = async (page: number, pageSize: number) => {
        // Note: Assuming the API supports pagination parameters
        return await diseaseAPI.getUserSymptomsforPDF(
          this.userId,
          page,
          pageSize
        );
      };

      let responseData: UserSymptomEntry[];

      if (isfetchLatest) {
        const response: SymptomsResponse =
          await diseaseAPI.getUserSymptomsforPDF(this.userId, 1, 1);
        responseData = response.data;
      } else if (isfetchAll) {
        responseData = await this.fetchAllPaginatedData(fetchFunction, false);
      } else {
        // Default behavior - fetch latest
        const response: SymptomsResponse =
          await diseaseAPI.getUserSymptomsforPDF(this.userId, 1, 1);
        responseData = response.data;
      }

      const processedEntries: ProcessedSymptomEntry[] = responseData.map(
        (entry) => ({
          id: entry.id,
          userId: entry.attributes.user_id,
          createdAt: entry.attributes.createdAt,
          updatedAt: entry.attributes.updatedAt,
          symptoms: entry.attributes.symptoms.data.map((symptom) => ({
            id: symptom.id,
            name: symptom.attributes.name,
            createdAt: symptom.attributes.createdAt,
            updatedAt: symptom.attributes.updatedAt,
          })),
        })
      );

      return processedEntries;
    } catch (error) {
      console.error("Error fetching user symptoms:", error);
      return [];
    }
  };

  private getPastReportsUploaded = async (
    analyticsType: string,
    isfetchLatest: boolean = false,
    isfetchAll: boolean = false
  ): Promise<ProcessedReport[]> => {
    try {
      let responseData: ReportEntry[];

      if (isfetchLatest) {
        const response: ReportsResponse =
          await diseaseAPI.getPastUploadedDocumentation(
            this.userId,
            this.client,
            1,
            1,
            analyticsType
          );
        responseData = response.data;
      } else if (isfetchAll) {
        const fetchFunction = async (page: number, pageSize: number) => {
          return await diseaseAPI.getPastUploadedDocumentation(
            this.userId,
            this.client,
            page,
            pageSize,
            analyticsType
          );
        };
        responseData = await this.fetchAllPaginatedData(fetchFunction, false);
      } else {
        // Default behavior - fetch latest
        const response: ReportsResponse =
          await diseaseAPI.getPastUploadedDocumentation(
            this.userId,
            this.client,
            1,
            1,
            analyticsType
          );
        responseData = response.data;
      }

      const processedReports: ProcessedReport[] = responseData.map(
        (report) => ({
          id: report.id,
          createdAt: report.attributes.createdAt,
          updatedAt: report.attributes.updatedAt,
          publishedAt: report.attributes.publishedAt,
          imageDescription: report.attributes.image_description,
          mediaType: report.attributes.media_type,
          analysisType: report.attributes.analysis_type,
          isVisible: report.attributes.is_visible,
          userId:
            report.attributes.user_report_individual_detail.data.attributes
              .user_id,
          userDetails: {
            name: report.attributes.user_report_individual_detail.data
              .attributes.name,
            age: report.attributes.user_report_individual_detail.data.attributes
              .age,
            gender:
              report.attributes.user_report_individual_detail.data.attributes
                .gender,
            phoneNumber:
              report.attributes.user_report_individual_detail.data.attributes
                .phone_number,
            email:
              report.attributes.user_report_individual_detail.data.attributes
                .email,
            weight:
              report.attributes.user_report_individual_detail.data.attributes
                .weight_in_kg,
            height:
              report.attributes.user_report_individual_detail.data.attributes
                .height_in_cms,
            waistCircumference:
              report.attributes.user_report_individual_detail.data.attributes
                .waist_circumference_in_cms,
            employmentStatus:
              report.attributes.user_report_individual_detail.data.attributes
                .employment_status,
            employmentType:
              report.attributes.user_report_individual_detail.data.attributes
                .employment_type,
          },
          mediaFiles: report.attributes.media.data.map((media) => ({
            id: media.id,
            name: media.attributes.name,
            url: media.attributes.url,
            size: media.attributes.size,
            mimeType: media.attributes.mime,
            extension: media.attributes.ext,
          })),
        })
      );

      return processedReports;
    } catch (error) {
      console.error(`Error fetching ${analyticsType} reports:`, error);
      return [];
    }
  };

  private getRecommendedDoctors = async (
    fetchTopDoctors: boolean = false
  ): Promise<ProcessedDoctor[]> => {
    try {
      const response: DoctorsResponse = await diseaseAPI.getAllDcotors();

      if (!response || !response.data || !Array.isArray(response.data.rows)) {
        return [];
      }

      const processedDoctors: ProcessedDoctor[] = response.data.rows.map(
        (doctor) => ({
          userId: doctor.userId,
          displayName: doctor.displayName,
          firstName: doctor.firstName,
          lastName: doctor.lastName,
          fullName: `${doctor.firstName} ${doctor.lastName}`.trim(),
          email: doctor.email,
          phone: doctor.phone,
          role: doctor.role,
          team: doctor.team,
          teamId: doctor.teamId,
          department: {
            id: doctor.teamDetails.Team.id,
            name: doctor.teamDetails.Team.name,
            description: doctor.teamDetails.Team.desc,
          },
          isActive: doctor.active && doctor.enabled && !doctor.isBanned,
          isHeadDoctor: doctor.teamDetails.headDoctor,
          imageUrl: doctor.imageUrl,
        })
      );

      // Filter to get only active doctors
      const activeDoctors = processedDoctors.filter(
        (doctor) => doctor.isActive
      );

      // If fetchTopDoctors is true, return limited number of doctors
      if (fetchTopDoctors) {
        // Return top doctors (head doctors first, then by role)
        const sortedDoctors = activeDoctors.sort((a, b) => {
          // Prioritize head doctors
          if (a.isHeadDoctor && !b.isHeadDoctor) return -1;
          if (!a.isHeadDoctor && b.isHeadDoctor) return 1;

          // Then prioritize doctors over other roles
          if (a.role === "Doctor" && b.role !== "Doctor") return -1;
          if (a.role !== "Doctor" && b.role === "Doctor") return 1;

          return 0;
        });

        // Return minimum of available doctors or a reasonable top count (e.g., 5)
        const topCount = Math.min(sortedDoctors.length, 5);
        return sortedDoctors.slice(0, topCount);
      }

      return activeDoctors;
    } catch (error) {
      console.error("Error fetching recommended doctors:", error);
      return [];
    }
  };

  // In dashboardHelper.ts - UPDATE ONLY the getAppointmentsBooked method

  private getAppointmentsBooked = async (
    isFetchLatest: boolean = false
  ): Promise<ProcessedAppointment[]> => {
    try {
      // Call both APIs in parallel for better performance
      // Use Promise.allSettled to handle partial failures gracefully
      const [appointmentsResult, doctorsResult] = await Promise.allSettled([
        diseaseAPI.getAllAppointmentsBookedForUser(this.userId),
        diseaseAPI.getAllDcotors(),
      ]);

      // Handle appointments API failure
      if (appointmentsResult.status === "rejected") {
        console.error(
          "❌ Failed to fetch appointments:",
          appointmentsResult.reason
        );
        return [];
      }

      const appointmentsResponse = appointmentsResult.value;

      // Check if appointments response is valid
      if (
        !appointmentsResponse?.data ||
        !Array.isArray(appointmentsResponse.data) ||
        appointmentsResponse.data.length === 0
      ) {
        return [];
      }

      // Create doctor lookup map for O(1) access - OPTIMIZATION
      const doctorsMap = new Map<number, string>();

      // Handle doctors API failure gracefully
      if (doctorsResult.status === "rejected") {
        console.warn("⚠️ Failed to fetch doctors list:", doctorsResult.reason);
        console.warn(
          "⚠️ Will use appointment.title as fallback for doctor names"
        );
      } else {
        const doctorsResponse = doctorsResult.value;

        // Safely access doctors data with multiple fallbacks
        if (
          doctorsResponse?.data?.rows &&
          Array.isArray(doctorsResponse.data.rows)
        ) {
          doctorsResponse.data.rows.forEach((doctor: any) => {
            if (doctor.userId && (doctor.firstName || doctor.lastName)) {
              const fullName = `${doctor.firstName || ""} ${
                doctor.lastName || ""
              }`.trim();
              if (fullName) {
                // Only add if we have a non-empty name
                doctorsMap.set(doctor.userId, fullName);
              }
            }
          });
          console.log(
            `✅ Loaded ${doctorsMap.size} doctor names for appointment mapping`
          );
        } else {
          console.warn("⚠️ Doctors API returned invalid data structure");
        }
      }

      // Transform the response data into processed format
      const processedAppointments: ProcessedAppointment[] =
        appointmentsResponse.data.map((appointment: any, index: number) => {
          // Get doctor name from the map - O(1) lookup with multiple fallbacks
          const doctorFromMap = doctorsMap.get(appointment.UserId);
          let doctorName = "Doctor"; // Ultimate fallback

          if (doctorFromMap) {
            // Found doctor in our map
            doctorName = doctorFromMap;
          } else if (appointment.title && appointment.title.trim()) {
            // Fallback to appointment title if available
            doctorName = appointment.title.trim();
          } else {
            // Log when we couldn't find doctor name anywhere
            console.warn(
              `⚠️ No doctor name found for appointment ${appointment.id} with UserId ${appointment.UserId}`
            );
          }

          const processed: ProcessedAppointment = {
            appointmentId: appointment.id,
            caseId: appointment.CaseId,
            userId: appointment.UserId,
            doctorName: doctorName, // CHANGED: Now uses actual doctor name instead of appointment.title
            appointmentLink: appointment.appointmentLink || "",
            appointmentDetails: {
              appointmentDate: epochToISTDate(appointment.appointmentDate),
              startTime: epochToIST(appointment.startTime),
              endTime: epochToIST(appointment.endTime),
              appointmentDateRaw: appointment.appointmentDate,
              startTimeRaw: appointment.startTime,
              endTimeRaw: appointment.endTime,
            },
            patientDetails: {
              patientId: appointment.Patient?.patientId || "",
              patientName: appointment.Patient
                ? `${appointment.Patient.firstName || ""} ${
                    appointment.Patient.lastName || ""
                  }`.trim()
                : "",
              patientMobileNo: appointment.Patient?.mobile || "",
              patientGender: appointment.Patient?.gender || "",
              imageUrl: appointment.Patient?.imageUrl || "",
            },
          };

          return processed;
        });

      // Sort appointments by start time (latest first)
      const sortedAppointments = processedAppointments.sort((a, b) => {
        // Primary sort: by appointment start time (latest first)
        const aStartTime = a.appointmentDetails.startTimeRaw || 0;
        const bStartTime = b.appointmentDetails.startTimeRaw || 0;

        // Convert to milliseconds if needed (epoch timestamps < year 2000 are likely in seconds)
        const aStartTimeMs =
          aStartTime < 1000000000000 ? aStartTime * 1000 : aStartTime;
        const bStartTimeMs =
          bStartTime < 1000000000000 ? bStartTime * 1000 : bStartTime;

        // Sort in descending order (latest first)
        if (bStartTimeMs !== aStartTimeMs) {
          return bStartTimeMs - aStartTimeMs;
        }

        // Secondary sort: by appointment date if start times are the same
        const aDate = a.appointmentDetails.appointmentDateRaw || 0;
        const bDate = b.appointmentDetails.appointmentDateRaw || 0;

        const aDateMs = aDate < 1000000000000 ? aDate * 1000 : aDate;
        const bDateMs = bDate < 1000000000000 ? bDate * 1000 : bDate;

        return bDateMs - aDateMs;
      });

      // Apply isFetchLatest filter ONLY if explicitly requested
      if (isFetchLatest && sortedAppointments.length > 0) {
        return [sortedAppointments[0]];
      }

      return sortedAppointments;
    } catch (error) {
      console.error("❌ Error fetching appointments:", error);

      // Log more details about the error
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }

      // Check if it's a network error
      if (error && typeof error === "object" && "response" in error) {
        console.error("API response error:", (error as any).response);
      }

      return [];
    }
  };

  private getRecentPrescriptions = async (): Promise<
    ProcessedPrescription[]
  > => {
    try {
      const response: PrescriptionEntry[] =
        await diseaseAPI.getUserPrescription(this.userId);

      // Check if response exists and has data
      if (!response || !Array.isArray(response) || response.length === 0) {
        return [];
      }

      // Process each prescription
      const processedPrescriptions: ProcessedPrescription[] = response.map(
        (prescription) => {
          // Parse food arrays from JSON strings with error handling
          let foodToAvoid: string[] = [];
          let foodToEat: string[] = [];

          try {
            if (prescription.foodToAvoid) {
              foodToAvoid = JSON.parse(prescription.foodToAvoid);
            }
          } catch (error) {
            console.warn(
              `Error parsing foodToAvoid for prescription ${prescription.id}:`,
              error
            );
            foodToAvoid = [];
          }

          try {
            if (prescription.foodToEat) {
              foodToEat = JSON.parse(prescription.foodToEat);
            }
          } catch (error) {
            console.warn(
              `Error parsing foodToEat for prescription ${prescription.id}:`,
              error
            );
            foodToEat = [];
          }

          // Group medicines by type
          const medicinesByType = {
            agni:
              prescription.Medicines?.filter((med) => med.type === "agni") ||
              [],
            dosha:
              prescription.Medicines?.filter((med) => med.type === "dosha") ||
              [],
            manovat:
              prescription.Medicines?.filter((med) => med.type === "manovat") ||
              [],
            saundarya:
              prescription.Medicines?.filter(
                (med) => med.type === "saundarya"
              ) || [],
            others:
              prescription.Medicines?.filter(
                (med) =>
                  !["agni", "dosha", "manovat", "saundarya"].includes(med.type)
              ) || [],
          };

          // Process medicines data
          const processedMedicines = (prescription.Medicines || []).map(
            (medicine) => ({
              id: medicine.id,
              name: medicine.name,
              dosage: medicine.dosage,
              frequency: medicine.frequency,
              instruction: medicine.instruction,
              type: medicine.type,
              createdAt: medicine.createdAt,
              updatedAt: medicine.updatedAt,
            })
          );

          // Format dates for display
          const nextFollowupFormatted = prescription.nextFollowup
            ? new Date(prescription.nextFollowup).toLocaleDateString("en-IN", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            : "";

          const createdAtFormatted = new Date(
            prescription.createdAt
          ).toLocaleDateString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });

          return {
            id: prescription.id,
            caseId: prescription.CaseId,
            nextFollowup: prescription.nextFollowup,
            nextFollowupFormatted,
            foodToAvoid,
            foodToEat,
            createdAt: prescription.createdAt,
            updatedAt: prescription.updatedAt,
            createdAtFormatted,
            medicines: processedMedicines,
            medicinesByType,
            totalMedicines: processedMedicines.length,
          };
        }
      );

      // Sort by createdAt in descending order (most recent first)
      const sortedPrescriptions = processedPrescriptions.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA; // Descending order (newest first)
      });

      return sortedPrescriptions;
    } catch (error) {
      console.error("Error fetching recent prescriptions:", error);

      // Log additional error details if available
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }

      return [];
    }
  };
  private getPrakritiResults = async (): Promise<PrakritiResult | null> => {
    try {
      // 1. Call the API
      const response: PrakritiApiResponse = await diseaseAPI.getAllScores(
        this.userId
      );

      // 2. Check if Prakriti data exists in recommendations
      if (
        !response ||
        !response.recommendations ||
        !response.recommendations.Prakriti
      ) {
        console.warn("Prakriti data not found in API response");
        return {
          name: "Prakriti",
          status: "Pending",
          color: "rgba(128, 128, 128, 1)", // Blackish/gray color for pending
        };
      }

      // 3. Get the Prakriti data from recommendations
      const prakritiData = response.recommendations.Prakriti;

      // 4. Check for question_responses presence and length
      const hasQuestionResponses =
        prakritiData.question_responses &&
        Array.isArray(prakritiData.question_responses) &&
        prakritiData.question_responses.length > 0;

      const hasQuestions =
        prakritiData.questions &&
        Array.isArray(prakritiData.questions) &&
        prakritiData.questions.length > 0;

      // Case 1: No question_responses at all
      if (!hasQuestionResponses) {
        return {
          name: "Prakriti",
          status: "Pending",
          color: "rgba(128, 128, 128, 1)", // Blackish/gray color for pending
          totalQuestions: hasQuestions ? prakritiData.questions.length : 0,
          answeredQuestions: 0,
        };
      }

      // Case 2: Has question_responses but length doesn't match questions length
      if (
        hasQuestions &&
        prakritiData.question_responses.length !== prakritiData.questions.length
      ) {
        const progress = `${prakritiData.question_responses.length}/${prakritiData.questions.length}`;

        // If we have a label, use the original color for that label
        // Otherwise, use greyish color
        const hasLabel = prakritiData.label && prakritiData.label.trim() !== "";
        const partialColor = hasLabel
          ? getColorByScoreLabel(prakritiData.score_label)
          : "rgba(128, 128, 128, 1)"; // Greyish color when no label

        return {
          name: "Prakriti",
          label: prakritiData.label || undefined,
          score_label: prakritiData.score_label || undefined,
          status: "Partial",
          color: partialColor,
          progress: progress,
          totalQuestions: prakritiData.questions.length,
          answeredQuestions: prakritiData.question_responses.length,
        };
      }

      // Case 3: Complete - question_responses.length === questions.length
      if (
        hasQuestions &&
        prakritiData.question_responses.length === prakritiData.questions.length
      ) {
        return {
          name: "Prakriti",
          label: prakritiData.label || undefined,
          score_label: prakritiData.score_label || undefined,
          status: "Complete",
          color: getColorByScoreLabel(prakritiData.score_label),
          totalQuestions: prakritiData.questions.length,
          answeredQuestions: prakritiData.question_responses.length,
        };
      }

      // Fallback case - if we can't determine the status clearly
      console.warn("Prakriti data structure doesn't match expected format");
      return {
        name: "Prakriti",
        label: prakritiData.label || undefined,
        score_label: prakritiData.score_label || undefined,
        status: "Pending",
        color: "rgba(128, 128, 128, 1)",
        totalQuestions: hasQuestions ? prakritiData.questions.length : 0,
        answeredQuestions: hasQuestionResponses
          ? prakritiData.question_responses.length
          : 0,
      };
    } catch (error) {
      // Log the error for debugging
      console.error("❌ Error fetching Prakriti results:", error);

      // Handle different types of errors
      if (error instanceof TypeError) {
        console.error("Network or parsing error occurred");
      } else if (error instanceof Error) {
        console.error("Error message:", error.message);
      }

      // Return a default pending state on error
      return {
        name: "Prakriti",
        status: "Pending",
        color: "rgba(128, 128, 128, 1)",
      };
    }
  };

  private getHealthScores = async (): Promise<HealthScoreResult[]> => {
    try {
      // 1. Define scores to remove from the groups
      const scoreToRemove = [
        "Physical Activity Score",
        "Sleep Score",
        "Dietary Pattern Assessment",
        "Stress Score",
      ];

      // Filter healthScoreGroups to exclude daily access scores
      const filteredScores: HealthScoreGroupItem[] =
        healthScoreGroups[0].filter((data: HealthScoreGroupItem) => {
          return !scoreToRemove.includes(data.name);
        });

      // 2. Fetch the API data
      const response: HealthScoresApiResponse = await diseaseAPI.getAllScores(
        this.userId
      );

      // 3. Check if recommendations exist
      if (!response || !response.recommendations) {
        console.warn("Health scores data not found in API response");
        return filteredScores.map((scoreConfig: HealthScoreGroupItem) => ({
          name: scoreConfig.name,
          status: "Pending" as const,
          color: getScoreColor(""),
          logo: scoreConfig.logo,
        }));
      }

      // 4. Process each filtered score
      const processedScores: HealthScoreResult[] = filteredScores.map(
        (scoreConfig: HealthScoreGroupItem) => {
          const scoreData = response.recommendations[scoreConfig.name];
          return processHealthScore(scoreConfig, scoreData);
        }
      );

      return processedScores;
    } catch (error) {
      // Log the error for debugging
      console.error("Error fetching health scores:", error);

      // Handle different types of errors
      if (error instanceof TypeError) {
        console.error("Network or parsing error occurred");
      } else if (error instanceof Error) {
        console.error("Error message:", error.message);
      }

      // Return default pending states for expected scores
      const scoreToRemove = [
        "Physical Activity Score",
        "Sleep Score",
        "Dietary Pattern Assessment",
        "Stress Score",
      ];

      const filteredScores: HealthScoreGroupItem[] =
        healthScoreGroups[0]?.filter((data: HealthScoreGroupItem) => {
          return !scoreToRemove.includes(data.name);
        }) || [];

      return filteredScores.map((scoreConfig: HealthScoreGroupItem) => ({
        name: scoreConfig.name,
        status: "Pending" as const,
        color: getScoreColor("Medium"),
        logo: scoreConfig.logo,
      }));
    }
  };

  private getDailyAccessHealthScores = async (): Promise<
    HealthScoreResult[]
  > => {
    try {
      // 1. Define daily health score groups to keep
      const dailyHealthScoreGroups = [
        "Physical Activity Score",
        "Sleep Score",
        "Dietary Pattern Assessment",
        "Stress Score",
      ];

      // Filter to keep only daily scores
      const filteredDailyScores: HealthScoreGroupItem[] =
        healthScoreGroups[0].filter((data: HealthScoreGroupItem) => {
          return dailyHealthScoreGroups.includes(data.name);
        });

      // 2. Call the API
      const response: HealthScoresApiResponse = await diseaseAPI.getAllScores(
        this.userId
      );

      // 3. Check if recommendations exist
      if (!response || !response.recommendations) {
        console.warn("Daily health scores data not found in API response");
        return filteredDailyScores.map((scoreConfig: HealthScoreGroupItem) => ({
          name: scoreConfig.name,
          status: "Pending" as const,
          color: getScoreColor(""),
          logo: scoreConfig.logo,
        }));
      }

      // 4. Process each daily score
      const processedDailyScores: HealthScoreResult[] = filteredDailyScores.map(
        (scoreConfig: HealthScoreGroupItem) => {
          const scoreData = response.recommendations[scoreConfig.name];
          return processHealthScore(scoreConfig, scoreData);
        }
      );

      return processedDailyScores;
    } catch (error) {
      // Log the error for debugging
      console.error("Error fetching daily health scores:", error);

      // Handle different types of errors
      if (error instanceof TypeError) {
        console.error("Network or parsing error occurred");
      } else if (error instanceof Error) {
        console.error("Error message:", error.message);
      }

      // Return default pending states for daily scores
      const dailyHealthScoreGroups = [
        "Physical Activity Score",
        "Sleep Score",
        "Dietary Pattern Assessment",
        "Stress Score",
      ];

      const filteredDailyScores: HealthScoreGroupItem[] =
        healthScoreGroups[0]?.filter((data: HealthScoreGroupItem) => {
          return dailyHealthScoreGroups.includes(data.name);
        }) || [];

      return filteredDailyScores.map((scoreConfig: HealthScoreGroupItem) => ({
        name: scoreConfig.name,
        status: "Pending" as const,
        color: getScoreColor("Medium"),
        logo: scoreConfig.logo,
      }));
    }
  };

  private getAyurVedicCorner = async (): Promise<AyurvedicCornerResult[]> => {
    try {
      const ayurvedicCornerMapping: Record<string, AyurvedicCornerConfig> = {
        "Agni Strength": {
          scoreName: "Medical History 2",
          quection_id: "Eira_Clinic_7",
          maxScore: 100,
          responseWithMap: [
            {
              response: "1-2 Hours",
              score: 62.5,
              colorCode: "rgba(255, 209, 80, 1)",
              output: "Tikshnagni",
              description:
                "Digestion works well all the time. Food is properly broken down, no bloating, no gas, and no leftover toxins. It shows your body is in balance and healthy state & no doshas are disturbed.",
            },
            {
              response: "3-4 Hours",
              score: 100,
              colorCode: "rgba(41, 191, 69, 1)",
              output: "Samagni",
              description:
                "Sometimes digestion is strong, other times it's weak. You may feel bloated one day and hungry the next. It is caused by Vata imbalance. Leads to irregular digestion and problems like gas, bloating, or constipation.",
            },
            {
              response: "5-6 Hours",
              score: 25,
              colorCode: "rgba(235, 0, 0, 1)",
              output: "Mandagni",
              description:
                "Digestion is slow and sluggish. Even light food feels heavy. You feel tired and may gain weight easily. It is caused by Kapha imbalance. Toxins (ama) can build up and lower your energy and immunity.",
            },
            {
              response: "More Than 6 Hours",
              score: 25,
              colorCode: "rgba(235, 0, 0, 1)",
              output: "Mandagni",
              description:
                "Digestion is slow and sluggish. Even light food feels heavy. You feel tired and may gain weight easily. It is caused by Kapha imbalance. Toxins (ama) can build up and lower your energy and immunity.",
            },
          ],
        },
        "Ojas Vitality Layer": {
          scoreName: "Prakriti",
          quection_id: "PR_23",
          maxScore: 100,
          responseWithMap: [
            {
              response: "Very good (rarely get sick)",
              score: 100,
              colorCode: "rgba(41, 191, 69, 1)",
              output: "Ojas Balanced",
              description:
                "Rare in current lifestyles, seen in well-balanced individuals with sattvic habits and good genetics.",
            },
            {
              response: "Varies with weather or specific conditions",
              score: 62.5,
              colorCode: "rgba(255, 209, 80, 1)",
              output: "Moderate Strength of Ojas",
              description:
                "Most common in people, stable health when lifestyle is balanced, but vulnerable to stress and overwork.",
            },
            {
              response: "Very poor (frequently get sick)",
              score: 25,
              colorCode: "rgba(235, 0, 0, 1)",
              output: "Low Quality or Deficient Ojas",
              description:
                "Seen in chronic illness, overexertion, poor nutrition, or long-standing stress. Needs rejuvenation (Rasayana therapy).",
            },
          ],
        },
      };

      const results: AyurvedicCornerResult[] = [];

      type AyurvedicApiResponse = ApiResponse<ApiResponseData>;
      // Process both categories
      const promises = Object.entries(ayurvedicCornerMapping).map(
        async ([categoryName, config]): Promise<AyurvedicCornerResult> => {
          try {
            // Call the API for each category
            const apiResponse: AyurvedicApiResponse =
              await diseaseAPI.getIndividualQuestionResponse(
                this.userId,
                config.scoreName,
                1,
                1,
                config.quection_id
              );

            // Validate API response

            if (
              !apiResponse?.data ||
              !Array.isArray(apiResponse.data) ||
              apiResponse.data.length === 0
            ) {
              console.warn(`No data found for category: ${categoryName}`);
              return {
                category: categoryName,
                score: 0,
                maxScore: config.maxScore,
                colorCode: "rgba(128, 128, 128, 1)",
                output: "No Data",
                response: "No response found",
                description: "No data available for this category",
                questionText: "",
                error: "No data found",
              };
            }

            const responseData = apiResponse.data[0];

            // Parse the response value
            let userResponseValue: string;
            try {
              const parsedResponse = JSON.parse(
                responseData.attributes.response
              );

              // Handle response format {"value": "1"} or {"value": "0"}
              if (
                typeof parsedResponse === "object" &&
                parsedResponse !== null &&
                "value" in parsedResponse
              ) {
                // Convert the string value to integer, then use as array index
                const valueIndex = parseInt(String(parsedResponse.value)); // This will be 0, 1, 2, etc.
                const questionValues =
                  responseData.attributes.scores_questionnaire.data.attributes
                    .question_values;

                if (valueIndex >= 0 && valueIndex < questionValues.length) {
                  userResponseValue = questionValues[valueIndex];
                } else {
                  throw new Error(
                    `Invalid response index: ${valueIndex} for question with ${questionValues.length} options`
                  );
                }
              } else if (typeof parsedResponse === "string") {
                userResponseValue = parsedResponse;
              } else {
                throw new Error("Unexpected response format");
              }
            } catch (parseError) {
              console.error(
                `❌ Error parsing response for ${categoryName}:`,
                parseError
              );
              console.error(`Raw response:`, responseData.attributes.response);
              throw new Error(
                `Failed to parse user response: ${
                  parseError instanceof Error
                    ? parseError.message
                    : "Unknown error"
                }`
              );
            }

            // Find matching response mapping
            const matchingMapping = config.responseWithMap.find(
              (mapping: ResponseMapping) =>
                mapping.response === userResponseValue
            );

            if (!matchingMapping) {
              console.warn(
                `⚠️ No matching mapping found for response: "${userResponseValue}" in category: ${categoryName}`
              );
              console.warn(
                `Available responses:`,
                config.responseWithMap.map((m) => m.response)
              );
              return {
                category: categoryName,
                score: 0,
                maxScore: config.maxScore,
                colorCode: "rgba(128, 128, 128, 1)",
                output: "Unknown",
                response: userResponseValue,
                description:
                  "No matching configuration found for this response",
                questionText:
                  responseData.attributes.scores_questionnaire.data.attributes
                    .question_text,
                error: "No matching mapping found",
              };
            }

            // Return the successful result
            return {
              category: categoryName,
              score: matchingMapping.score,
              maxScore: config.maxScore,
              colorCode: matchingMapping.colorCode,
              output: matchingMapping.output,
              response: matchingMapping.response,
              description: matchingMapping.description,
              questionText:
                responseData.attributes.scores_questionnaire.data.attributes
                  .question_text,
            };
          } catch (error) {
            console.error(
              `❌ Error processing category ${categoryName}:`,
              error
            );
            return {
              category: categoryName,
              score: 0,
              maxScore: config.maxScore,
              colorCode: "rgba(235, 0, 0, 1)",
              output: "Error",
              response: "Error occurred",
              description: `Failed to process data: ${
                error instanceof Error ? error.message : "Unknown error"
              }`,
              questionText: "",
              error: error instanceof Error ? error.message : "Unknown error",
            };
          }
        }
      );

      const resolvedResults = await Promise.all(promises);
      results.push(...resolvedResults);

      return results;
    } catch (error) {
      console.error("❌ Error fetching ayurvedic corner data:", error);

      // Return error results for both categories
      return [
        {
          category: "Agni Strength",
          score: 0,
          maxScore: 100,
          colorCode: "rgba(235, 0, 0, 1)",
          output: "Error",
          response: "System Error",
          description: `System error occurred: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          questionText: "",
          error: error instanceof Error ? error.message : "Unknown error",
        },
        {
          category: "Ojas Vitality Layer",
          score: 0,
          maxScore: 100,
          colorCode: "rgba(235, 0, 0, 1)",
          output: "Error",
          response: "System Error",
          description: `System error occurred: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
          questionText: "",
          error: error instanceof Error ? error.message : "Unknown error",
        },
      ];
    }
  };

  private getHealthHistory = async (): Promise<HealthHistoryData | null> => {
    try {
      // Define score names with prefixes for Health History
      const scoreNamesWithPrefixes: ScoreNameWithPrefix[] = [
        {
          scoreName: "Medical History 1",
          respondedQuestionIds: "Eira_Clinic_",
        },
        {
          scoreName: "Medical History 2",
          respondedQuestionIds: "Eira_Clinic_",
        },
      ];

      // Use the scorehelper function to get health history
      const healthHistoryResult = await getHealthHistory(
        this.userId,
        this.client,
        scoreNamesWithPrefixes
      );

      if (!healthHistoryResult) {
        return {
          result: [],
          respondedQuestionIds: [],
          totalQuestions: 0,
          isComplete: false,
          answeredQuestions: 0,
        };
      }

      const { result, respondedQuestionIds, totalQuestions } =
        healthHistoryResult;

      // Determine if health history is complete
      const isComplete = respondedQuestionIds.length >= totalQuestions;
      const answeredQuestions = respondedQuestionIds.length;

      return {
        result,
        respondedQuestionIds,
        totalQuestions,
        isComplete,
        answeredQuestions,
      };
    } catch (error) {
      console.error("❌ Error fetching health history:", error);

      // Log more details about the error
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }

      // Return default empty state on error
      return {
        result: [],
        respondedQuestionIds: [],
        totalQuestions: 0,
        isComplete: false,
        answeredQuestions: 0,
      };
    }
  };

  // Public methods
  // Fixed getAllDashboardData method
  public getAllDashboardData = async (
    loadDataFor?: (
      | "UserDetails"
      | "Prakriti"
      | "Symtoms"
      | "BloodReport"
      | "TongueImage"
      | "HealthScore"
      | "DcotorsList"
      | "Appointments"
      | "Prescription"
      | "DailyScores"
      | "AyurvedaCorner"
      | "HealthHistory"
    )[]
  ): Promise<DashboardData> => {
    try {
      // If no specific data requested, load all data (backward compatibility)
      const dataToLoad = loadDataFor || [
        "UserDetails",
        "Prakriti",
        "Symtoms",
        "BloodReport",
        "TongueImage",
        "HealthScore",
        "DcotorsList",
        "Appointments",
        "Prescription",
        "DailyScores",
        "AyurvedaCorner",
        "HealthHistory",
      ];

      // Initialize default values for all properties
      const defaultData: DashboardData = {
        userDetails: null,
        prakritiData: null,
        symptoms: [],
        bloodReports: [],
        tongueImages: [],
        healthScores: [],
        doctors: [],
        appointments: [],
        prescriptions: [],
        dailyScores: [],
        ayurvedicCorner: [],
        healthHistory: null,
      };

      // Create promises array based on dataToLoad parameter
      const promises: Promise<any>[] = [];
      const promiseMap: { [key: string]: number } = {};

      let promiseIndex = 0;

      // Build promises array based on requested data types
      if (dataToLoad.includes("UserDetails")) {
        promises.push(this.getUserDetails());
        promiseMap["userDetails"] = promiseIndex++;
      }

      if (dataToLoad.includes("Prakriti")) {
        promises.push(this.getPrakritiResults());
        promiseMap["prakritiData"] = promiseIndex++;
      }

      if (dataToLoad.includes("Symtoms")) {
        promises.push(this.getUserDashboardSymptoms(false, true)); // Get all symptoms
        promiseMap["symptoms"] = promiseIndex++;
      }

      if (dataToLoad.includes("BloodReport")) {
        promises.push(this.getPastReportsUploaded("blood_report", false, true)); // Get all blood reports
        promiseMap["bloodReports"] = promiseIndex++;
      }

      if (dataToLoad.includes("TongueImage")) {
        promises.push(this.getPastReportsUploaded("tongue", false, true)); // Get all tongue images
        promiseMap["tongueImages"] = promiseIndex++;
      }

      if (dataToLoad.includes("HealthScore")) {
        promises.push(this.getHealthScores());
        promiseMap["healthScores"] = promiseIndex++;
      }

      if (dataToLoad.includes("DcotorsList")) {
        promises.push(this.getRecommendedDoctors(false)); // Get all doctors
        promiseMap["doctors"] = promiseIndex++;
      }

      if (dataToLoad.includes("Appointments")) {
        promises.push(this.getAppointmentsBooked(false)); // Get ALL appointments
        promiseMap["appointments"] = promiseIndex++;
      }

      if (dataToLoad.includes("Prescription")) {
        promises.push(this.getRecentPrescriptions());
        promiseMap["prescriptions"] = promiseIndex++;
      }

      if (dataToLoad.includes("DailyScores")) {
        promises.push(this.getDailyAccessHealthScores());
        promiseMap["dailyScores"] = promiseIndex++;
      }

      if (dataToLoad.includes("AyurvedaCorner")) {
        promises.push(this.getAyurVedicCorner());
        promiseMap["ayurvedicCorner"] = promiseIndex++;
      }

      if (dataToLoad.includes("HealthHistory")) {
        promises.push(this.getHealthHistory());
        promiseMap["healthHistory"] = promiseIndex++;
      }

      // Execute all promises and handle results
      const results = await Promise.allSettled(promises);

      // Map results back to the data object
      Object.entries(promiseMap).forEach(([key, index]) => {
        const result = results[index];
        if (result.status === "fulfilled") {
          (defaultData as any)[key] = result.value;
        } else {
          console.error(`❌ Error fetching ${key}:`, result.reason);
          // Keep default values for failed promises
        }
      });

      return defaultData;
    } catch (error) {
      console.error("💥 Critical error in getAllDashboardData:", error);
      throw error;
    }
  };

  // Add a new method specifically for getting latest appointments
  // public getLatestDashboardData = async (
  //   loadDataFor: (
  //     | "UserDetails"
  //     | "Prakriti"
  //     | "Symtoms"
  //     | "BloodReport"
  //     | "TongueImage"
  //     | "HealthScore"
  //     | "DcotorsList"
  //     | "Appointments"
  //     | "Prescription"
  //     | "DailyScores"
  //     | "AyurvedaCorner"
  //   )[],
  //   isFecthAll:boolean,
  // ): Promise<DashboardData> => {
  //   try {
  //     // Initialize default values for all properties
  //     const defaultData: DashboardData = {
  //       userDetails: null,
  //       prakritiData: null,
  //       symptoms: [],
  //       bloodReports: [],
  //       tongueImages: [],
  //       healthScores: [],
  //       doctors: [],
  //       appointments: [],
  //       prescriptions: [],
  //       dailyScores: [],
  //       ayurvedicCorner: [],
  //     };

  //     // Create promises array based on loadDataFor parameter
  //     const promises: Promise<any>[] = [];
  //     const promiseMap: { [key: string]: number } = {};

  //     let promiseIndex = 0;

  //     if (loadDataFor.includes("UserDetails")) {
  //       promises.push(this.getUserDetails());
  //       promiseMap["userDetails"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("Prakriti")) {
  //       promises.push(this.getPrakritiResults());
  //       promiseMap["prakritiData"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("Symtoms")) {
  //       promises.push(this.getUserDashboardSymptoms(false, true));
  //       promiseMap["symptoms"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("BloodReport")) {
  //       promises.push(this.getPastReportsUploaded("blood_report", true, false));
  //       promiseMap["bloodReports"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("TongueImage")) {
  //       promises.push(this.getPastReportsUploaded("tongue", true, false));
  //       promiseMap["tongueImages"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("HealthScore")) {
  //       promises.push(this.getHealthScores());
  //       promiseMap["healthScores"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("DcotorsList")) {
  //       promises.push(this.getRecommendedDoctors(true));
  //       promiseMap["doctors"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("Appointments")) {
  //       // For latest dashboard, get only the most recent appointment
  //       promises.push(this.getAppointmentsBooked());
  //       promiseMap["appointments"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("Prescription")) {
  //       promises.push(this.getRecentPrescriptions());
  //       promiseMap["prescriptions"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("DailyScores")) {
  //       promises.push(this.getDailyAccessHealthScores());
  //       promiseMap["dailyScores"] = promiseIndex++;
  //     }

  //     if (loadDataFor.includes("AyurvedaCorner")) {
  //       promises.push(this.getAyurVedicCorner());
  //       promiseMap["ayurvedicCorner"] = promiseIndex++;
  //     }

  //     // Execute all promises and handle results
  //     const results = await Promise.allSettled(promises);

  //     // Map results back to the data object
  //     Object.entries(promiseMap).forEach(([key, index]) => {
  //       const result = results[index];
  //       if (result.status === "fulfilled") {
  //         (defaultData as any)[key] = result.value;
  //       } else {
  //         console.error(`Error fetching ${key}:`, result.reason);
  //         // Keep default values for failed promises
  //       }
  //     });

  //     return defaultData;
  //   } catch (error) {
  //     console.error("Error fetching latest dashboard data:", error);
  //     throw error;
  //   }
  // };

  // Add a new method specifically for getting latest appointments
  public getLatestDashboardData = async (
    loadDataFor: (
      | "UserDetails"
      | "Prakriti"
      | "Symtoms"
      | "BloodReport"
      | "TongueImage"
      | "HealthScore"
      | "DcotorsList"
      | "Appointments"
      | "Prescription"
      | "DailyScores"
      | "AyurvedaCorner"
      | "HealthHistory"
    )[],
    isFetchAll: boolean = false
  ): Promise<Partial<DashboardData>> => {
    try {
      // Initialize empty result object - only add requested data
      const resultData: Partial<DashboardData> = {};

      // Create promises array based on loadDataFor parameter
      const promises: Promise<any>[] = [];
      const promiseMap: { [key: string]: number } = {};

      let promiseIndex = 0;

      // Build promises array based on requested data types with conditional fetching strategy
      if (loadDataFor.includes("UserDetails")) {
        promises.push(this.getUserDetails());
        promiseMap["userDetails"] = promiseIndex++;
      }

      if (loadDataFor.includes("Prakriti")) {
        promises.push(this.getPrakritiResults());
        promiseMap["prakritiData"] = promiseIndex++;
      }

      if (loadDataFor.includes("Symtoms")) {
        // For symptoms, use isFetchAll to determine strategy
        promises.push(this.getUserDashboardSymptoms(!isFetchAll, isFetchAll));
        promiseMap["symptoms"] = promiseIndex++;
      }

      if (loadDataFor.includes("BloodReport")) {
        // For blood reports, fetch latest unless isFetchAll is true
        promises.push(
          this.getPastReportsUploaded("blood_report", !isFetchAll, isFetchAll)
        );
        promiseMap["bloodReports"] = promiseIndex++;
        console.log(
          "📝 Added BloodReport to latest promises (fetchAll:",
          isFetchAll,
          ")"
        );
      }

      if (loadDataFor.includes("TongueImage")) {
        // For tongue images, fetch latest unless isFetchAll is true
        promises.push(
          this.getPastReportsUploaded("tongue", !isFetchAll, isFetchAll)
        );
        promiseMap["tongueImages"] = promiseIndex++;
      }

      if (loadDataFor.includes("HealthScore")) {
        promises.push(this.getHealthScores());
        promiseMap["healthScores"] = promiseIndex++;
      }

      if (loadDataFor.includes("DcotorsList")) {
        // For doctors, use top doctors unless isFetchAll is true
        promises.push(this.getRecommendedDoctors(!isFetchAll));
        promiseMap["doctors"] = promiseIndex++;
      }

      if (loadDataFor.includes("Appointments")) {
        // For appointments, fetch latest unless isFetchAll is true
        promises.push(this.getAppointmentsBooked(!isFetchAll));
        promiseMap["appointments"] = promiseIndex++;
      }

      if (loadDataFor.includes("Prescription")) {
        promises.push(this.getRecentPrescriptions());
        promiseMap["prescriptions"] = promiseIndex++;
      }

      if (loadDataFor.includes("DailyScores")) {
        promises.push(this.getDailyAccessHealthScores());
        promiseMap["dailyScores"] = promiseIndex++;
      }

      if (loadDataFor.includes("AyurvedaCorner")) {
        promises.push(this.getAyurVedicCorner());
        promiseMap["ayurvedicCorner"] = promiseIndex++;
      }

      if (loadDataFor.includes("HealthHistory")) {
        promises.push(this.getHealthHistory());
        promiseMap["healthHistory"] = promiseIndex++;
      }

      // Execute all promises and handle results
      const results = await Promise.allSettled(promises);

      // Map results back to the data object - only add requested data
      Object.entries(promiseMap).forEach(([key, index]) => {
        const result = results[index];
        if (result.status === "fulfilled") {
          (resultData as any)[key] = result.value;
        } else {
          console.error(`❌ Error fetching latest ${key}:`, result.reason);
          // Don't add anything to resultData for failed promises
        }
      });

      return resultData;
    } catch (error) {
      console.error("💥 Critical error in getLatestDashboardData:", error);
      throw error;
    }
  };

  // Legacy method for backward compatibility
  public getUserDashboardData = async (): Promise<DashboardData> => {
    return this.getAllDashboardData();
  };
}

export default UserDashboardData;
